import React, { useState, useMemo } from "react";
import {
  flexRender,
  getCoreRowModel,
  useReactTable,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
} from "@tanstack/react-table";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "./ui/table";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "./ui/card";
import {
  Trash,
  MagnifyingGlass,
  PlusCircle,
  BookOpen,
  CaretLeft,
  CaretRight,
  Hash,
} from "@phosphor-icons/react";
import { Loader2 } from "lucide-react";
import { GetAllCourses, useDeleteCourse } from "../adapters/Requests";
import useAxiosPrivate from "../hooks/useAxiosPrivate";
import {
  Dialog as DialogX,
  DialogContent as DialogContentX,
  DialogHeader,
  DialogTitle as DialogTitleX,
  DialogDescription,
  DialogFooter,
} from "./ui/dialog";
import { toast } from "sonner";

export default function Courses() {
  const {
    data: AllCourses,
    isLoading: coursesLoading,
    refetch: refetchCourses,
  } = GetAllCourses();

  const [globalFilter, setGlobalFilter] = useState("");
  const [sorting, setSorting] = useState([]);
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 10 });

  const [newCourse, setNewCourse] = useState("");
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [courseToDelete, setCourseToDelete] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isDeletingCourse, setIsDeletingCourse] = useState(false);


  const deleteCourse = useDeleteCourse();
  const apiClientPrivate = useAxiosPrivate();

  const tableData = useMemo(() => AllCourses || [], [AllCourses]);

  const isCourseDuplicate = () => {
    const normalizedNewCourse = newCourse.trim().toLowerCase();
    return tableData.some(
      (course) => course.name.trim().toLowerCase() === normalizedNewCourse
    );
  };

  const handleAddCourse = async () => {
    if (newCourse) {
      if (isCourseDuplicate()) {
        toast.error("Course already exists");
        return;
      }

      setLoading(true);
      try {
        await apiClientPrivate.post(`/vacancy/vacancy/courses`, {
          name: newCourse.trim(),
        });
        setNewCourse("");
        refetchCourses();
        toast.success("Course added successfully");
      } catch (error) {
        const errorMessage =
          error.response?.data?.message || "Error adding course";
        toast.error(errorMessage);
      } finally {
        setLoading(false);
      }
    }
  };

  const handleDeleteClick = (course) => {
    setCourseToDelete(course);
    setDeleteDialogOpen(true);
  };

  const confirmDeleteCourse = async () => {
    if (courseToDelete) {
      setIsDeletingCourse(true);
      try {
        await deleteCourse(courseToDelete.id);
        refetchCourses();
        toast.success("Course deleted successfully");
      } catch (error) {
        const status = error?.response?.status;
        const backendMessage =
          error?.response?.data?.error || error?.response?.data?.message;

        if (error.message === "Course not found" || status === 404) {
          toast.warning("Course not found. It may have been already deleted.");
        } else if (status === 409) {
          toast.error(
            backendMessage || "Course is being referenced and cannot be deleted."
          );
        } else {
          toast.error(backendMessage || "Error deleting course");
        }
      } finally {
        setIsDeletingCourse(false);
        setDeleteDialogOpen(false);
        setCourseToDelete(null);
      }
    }
  };

  const columns = [
    {
      id: "index",
      header: "#",
      cell: ({ row }) => (
        <span className="text-gray-500 font-mono text-xs">{row.index + 1}</span>
      ),
      size: 50,
    },
    {
      accessorKey: "name",
      header: "Course Name",
      cell: ({ row }) => (
        <div className="flex items-center gap-2 font-medium text-gray-900">
          <BookOpen className="text-green-600" size={18} />
          {row.getValue("name")}
        </div>
      ),
    },
    {
      id: "actions",
      cell: ({ row }) => (
        <div className="flex justify-end">
          <Button
            variant="ghost"
            size="sm"
            className="h-8 w-8 p-0 text-gray-400 hover:text-red-600 hover:bg-red-50"
            onClick={() => handleDeleteClick(row.original)}
          >
            <Trash size={18} />
          </Button>
        </div>
      ),
    },
  ];

  const table = useReactTable({
    data: tableData,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    onGlobalFilterChange: setGlobalFilter,
    onPaginationChange: setPagination,
    state: {
      globalFilter,
      pagination,
      sorting,
    },
    onSortingChange: setSorting,
  });

  return (
    <div className="w-full max-w-5xl mx-auto p-4">
      <Card className="border-slate-200 shadow-sm bg-white">
        <CardHeader className="border-b border-slate-100 bg-gray-50/40 p-6">
          <div className="flex flex-col md:flex-row justify-between md:items-center gap-4">
            <div>
              <CardTitle className="text-xl font-bold text-gray-900 tracking-tight">
                Course Management
              </CardTitle>
              <CardDescription>
                Add, view, and manage academic courses.
              </CardDescription>
            </div>

            <div className="flex flex-col sm:flex-row gap-3 items-center">
              <div className="relative w-full sm:w-64">
                <MagnifyingGlass
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                  size={14}
                />
                <Input
                  placeholder="Search courses..."
                  value={globalFilter ?? ""}
                  onChange={(e) => setGlobalFilter(e.target.value)}
                  className="pl-9 h-9 bg-white"
                />
              </div>
            </div>
          </div>

          <div className="mt-4 flex gap-2">
            <Input
              placeholder="Enter new course name..."
              value={newCourse}
              onChange={(e) => setNewCourse(e.target.value)}
              className="h-9 flex-1 bg-white"
              onKeyDown={(e) => e.key === "Enter" && handleAddCourse()}
            />
            <Button
              className="bg-green-600 hover:bg-green-700 text-white h-9 shadow-sm"
              onClick={handleAddCourse}
              disabled={loading || !newCourse.trim()}
            >
              {loading ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <PlusCircle className="mr-2 h-4 w-4" />
              )}
              Add Course
            </Button>
          </div>
        </CardHeader>

        <CardContent className="p-0">
          {coursesLoading ? (
            <div className="h-64 flex items-center justify-center">
              <Loader2 className="w-8 h-8 text-green-600 animate-spin" />
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader className="bg-gray-50/50">
                  {table.getHeaderGroups().map((headerGroup) => (
                    <TableRow
                      key={headerGroup.id}
                      className="hover:bg-transparent border-b border-slate-100"
                    >
                      {headerGroup.headers.map((header) => (
                        <TableHead
                          key={header.id}
                          className="h-10 text-xs font-semibold text-gray-500 uppercase tracking-wider"
                        >
                          {flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                        </TableHead>
                      ))}
                    </TableRow>
                  ))}
                </TableHeader>
                <TableBody>
                  {table.getRowModel().rows?.length ? (
                    table.getRowModel().rows.map((row) => (
                      <TableRow
                        key={row.id}
                        className="hover:bg-slate-50/60 transition-colors border-b border-slate-100"
                      >
                        {row.getVisibleCells().map((cell) => (
                          <TableCell key={cell.id} className="py-3">
                            {flexRender(
                              cell.column.columnDef.cell,
                              cell.getContext()
                            )}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell
                        colSpan={columns.length}
                        className="h-32 text-center text-gray-500"
                      >
                        No courses found.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>

        <div className="flex items-center justify-between p-3 border-t border-slate-100 bg-gray-50/30">
          <div className="text-xs text-gray-500">
            Page {table.getState().pagination.pageIndex + 1} of{" "}
            {table.getPageCount()}
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => table.previousPage()}
              disabled={!table.getCanPreviousPage()}
              className="h-7 w-20 bg-white text-xs"
            >
              <CaretLeft className="mr-1" size={12} /> Previous
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => table.nextPage()}
              disabled={!table.getCanNextPage()}
              className="h-7 w-20 bg-white text-xs"
            >
              Next <CaretRight className="ml-1" size={12} />
            </Button>
          </div>
        </div>
      </Card>

      <DialogX
        open={deleteDialogOpen}
        onOpenChange={(open) => !open && setDeleteDialogOpen(false)}
      >
        <DialogContentX>
          <DialogHeader>
            <DialogTitleX>Confirm Deletion</DialogTitleX>
            <DialogDescription>
              Are you sure you want to delete the course{" "}
              <span className="font-bold text-gray-900">
                {courseToDelete?.name}
              </span>
              ? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDeleteDialogOpen(false)}
              disabled={isDeletingCourse}
            >
              Cancel
            </Button>
            <Button
              className="bg-red-600 hover:bg-red-700 text-white"
              onClick={confirmDeleteCourse}
              disabled={isDeletingCourse}
            >
              {isDeletingCourse && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}
              Delete Course
            </Button>
          </DialogFooter>
        </DialogContentX>
      </DialogX>


    </div>
  );
}
